import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Star, Video } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UnifiedMonthGrid } from '@/components/calendar/UnifiedMonthGrid';
import { UnifiedWeekGrid } from '@/components/calendar/UnifiedWeekGrid';
import {
  DEFAULT_MILESTONE_FLAGS,
  EVENT_TYPE_CLASSES,
  EVENT_TYPE_LABELS,
  MILESTONE_LABELS,
  MilestoneFlags,
  UnifiedEvent,
  UnifiedEventType,
  useExpertNames,
  useMentorOptions,
  useUnifiedCalendar,
} from '@/hooks/useUnifiedCalendar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'unified_calendar_milestones';
const ONLY_EXPERT_KEY = 'unified_calendar_only_expert';

const LEGEND_TYPES: UnifiedEventType[] = [
  'cohort_workshop',
  'cohort_mentoring',
  'custom_mentorship_session',
];

export default function UnifiedCalendarPage() {
  const { user, roles } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = roles.includes('super_admin');

  const [view, setView] = useState<'month' | 'week'>('month');
  const [referenceDate, setReferenceDate] = useState(new Date());
  const [selectedMentors, setSelectedMentors] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [contextFilter, setContextFilter] = useState<string>('all');
  const [selectedEvent, setSelectedEvent] = useState<UnifiedEvent | null>(null);
  const [milestones, setMilestones] = useState<MilestoneFlags>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULT_MILESTONE_FLAGS, ...JSON.parse(raw) } : DEFAULT_MILESTONE_FLAGS;
    } catch {
      return DEFAULT_MILESTONE_FLAGS;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(milestones));
  }, [milestones]);

  const allMilestonesOn = Object.values(milestones).every(Boolean);
  const toggleAllMilestones = () =>
    setMilestones(
      Object.keys(DEFAULT_MILESTONE_FLAGS).reduce((acc, key) => {
        acc[key as keyof MilestoneFlags] = !allMilestonesOn;
        return acc;
      }, {} as MilestoneFlags),
    );

  const [onlyExpert, setOnlyExpert] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ONLY_EXPERT_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(ONLY_EXPERT_KEY, String(onlyExpert));
  }, [onlyExpert]);

  const { from, to } = useMemo(() => {
    if (view === 'month') {
      return {
        from: format(startOfWeek(startOfMonth(referenceDate), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        to: format(endOfWeek(endOfMonth(referenceDate), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    }
    return {
      from: format(startOfWeek(referenceDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      to: format(endOfWeek(referenceDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    };
  }, [view, referenceDate]);

  const includeAllCohorts = isSuperAdmin && selectedMentors.length === 0;

  const { data: mentorOptions = [] } = useMentorOptions(isSuperAdmin);
  const { data: events = [], isLoading } = useUnifiedCalendar({
    from,
    to,
    mentorIds: isSuperAdmin ? selectedMentors : [],
    restrictToSelf: !isSuperAdmin,
    includeAllCohorts,
    onlyExpert,
    milestones,
  });

  const expertIds = useMemo(
    () => Array.from(new Set(events.map((e) => e.expertId).filter(Boolean) as string[])),
    [events],
  );
  const { data: expertNames } = useExpertNames(expertIds);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    mentorOptions.forEach((m) => map.set(m.id, m.name));
    expertNames?.forEach((name, id) => {
      if (!map.has(id)) map.set(id, name);
    });
    return map;
  }, [mentorOptions, expertNames]);

  const contexts = useMemo(() => {
    const map = new Map<string, string>();
    events.forEach((e) => {
      if (e.contextId) map.set(e.contextId, e.contextLabel);
    });
    return Array.from(map.entries());
  }, [events]);

  const filtered = useMemo(
    () =>
      events.filter(
        (e) =>
          (typeFilter === 'all' || e.type === typeFilter) &&
          (contextFilter === 'all' || e.contextId === contextFilter),
      ),
    [events, typeFilter, contextFilter],
  );

  const mentorNameById = useMemo(
    () => new Map(mentorOptions.map((m) => [m.id, m.name])),
    [mentorOptions],
  );

  const shift = (dir: number) =>
    setReferenceDate((d) => (view === 'month' ? addMonths(d, dir) : addWeeks(d, dir)));

  const toggleMentor = (id: string) =>
    setSelectedMentors((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-primary" />
            Calendario Unificado
          </h1>
          <p className="text-sm text-muted-foreground">
            Sesiones en vivo y mentorías de cohortes, más las mentorías personalizadas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setReferenceDate(new Date())}>
            Hoy
          </Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Select value={view} onValueChange={(v) => setView(v as 'month' | 'week')}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mensual</SelectItem>
              <SelectItem value="week">Semanal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">Mostrar en el calendario</CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={toggleAllMilestones}>
                {allMilestonesOn ? 'Quitar todas' : 'Seleccionar todas'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {(Object.keys(MILESTONE_LABELS) as (keyof MilestoneFlags)[]).map((key) => (
                <label key={key} className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={milestones[key]}
                    onCheckedChange={(checked) =>
                      setMilestones((prev) => ({ ...prev, [key]: checked === true }))
                    }
                  />
                  <span className="leading-tight">{MILESTONE_LABELS[key]}</span>
                </label>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Leyenda</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {LEGEND_TYPES.map((type) => (
                <div key={type} className="flex items-center gap-2 text-sm">
                  <span className={cn('h-3 w-3 shrink-0 rounded border', EVENT_TYPE_CLASSES[type])} />
                  <span className="leading-tight">{EVENT_TYPE_LABELS[type]}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Filtros</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant={onlyExpert ? 'default' : 'outline'}
                size="sm"
                className="w-full justify-start gap-2"
                onClick={() => setOnlyExpert((v) => !v)}
              >
                <Star className="h-4 w-4" />
                Solo donde soy experto
              </Button>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={contextFilter} onValueChange={setContextFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Cohorte o proyecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las cohortes y proyectos</SelectItem>
                  {contexts.map(([id, label]) => (
                    <SelectItem key={id} value={id}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isSuperAdmin && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      {selectedMentors.length === 0
                        ? 'Todos los mentores'
                        : `${selectedMentors.length} mentor(es)`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <ScrollArea className="h-64 p-3">
                      <div className="space-y-2">
                        {mentorOptions.map((m) => (
                          <label key={m.id} className="flex items-start gap-2 text-sm">
                            <Checkbox
                              checked={selectedMentors.includes(m.id)}
                              onCheckedChange={() => toggleMentor(m.id)}
                            />
                            <span className="leading-tight">{m.name}</span>
                          </label>
                        ))}
                      </div>
                    </ScrollArea>
                    {selectedMentors.length > 0 && (
                      <div className="border-t border-border p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full"
                          onClick={() => setSelectedMentors([])}
                        >
                          Ver todos
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base capitalize">
              {view === 'month'
                ? format(referenceDate, "MMMM 'de' yyyy", { locale: es })
                : `Semana del ${format(startOfWeek(referenceDate, { weekStartsOn: 1 }), "d 'de' MMMM", { locale: es })}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Cargando…</p>
            ) : (
              <TooltipProvider>
                {view === 'month' ? (
                  <UnifiedMonthGrid
                    referenceDate={referenceDate}
                    events={filtered}
                    onSelect={setSelectedEvent}
                    nameById={nameById}
                  />
                ) : (
                  <UnifiedWeekGrid
                    referenceDate={referenceDate}
                    events={filtered}
                    onSelect={setSelectedEvent}
                    nameById={nameById}
                  />
                )}
              </TooltipProvider>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent>
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedEvent.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">{EVENT_TYPE_LABELS[selectedEvent.type]}</Badge>
                  {selectedEvent.modality && (
                    <Badge variant="outline">
                      {selectedEvent.modality === 'virtual' ? 'Virtual' : 'Presencial'}
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground">{selectedEvent.contextLabel}</p>
                <p>
                  {format(new Date(`${selectedEvent.date}T00:00:00`), "EEEE d 'de' MMMM 'de' yyyy", {
                    locale: es,
                  })}
                  {selectedEvent.startTime
                    ? ` · ${selectedEvent.startTime}${selectedEvent.endTime ? `–${selectedEvent.endTime}` : ''}`
                    : ' · Todo el día'}
                </p>
                {selectedEvent.description && (
                  <p className="text-muted-foreground">{selectedEvent.description}</p>
                )}
                {isSuperAdmin && selectedEvent.mentorIds.length > 0 && (
                  <p className="text-muted-foreground">
                    Mentores:{' '}
                    {selectedEvent.mentorIds
                      .map((id) => mentorNameById.get(id) ?? 'Mentor')
                      .join(', ')}
                  </p>
                )}
                {selectedEvent.expertId && (
                  <p className="text-muted-foreground">
                    Experto:{' '}
                    {expertNames?.get(selectedEvent.expertId) ??
                      mentorNameById.get(selectedEvent.expertId) ??
                      'Experto asignado'}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-2">
                  {selectedEvent.meetLink && (
                    <Button asChild size="sm" variant="outline">
                      <a href={selectedEvent.meetLink} target="_blank" rel="noreferrer">
                        <Video className="mr-1 h-4 w-4" /> Enlace de conexión
                      </a>
                    </Button>
                  )}
                  {selectedEvent.recordingLink && (
                    <Button asChild size="sm" variant="outline">
                      <a href={selectedEvent.recordingLink} target="_blank" rel="noreferrer">
                        <Video className="mr-1 h-4 w-4" /> Grabación
                      </a>
                    </Button>
                  )}
                  {selectedEvent.href && (
                    <Button
                      size="sm"
                      onClick={() => {
                        navigate(selectedEvent.href!);
                        setSelectedEvent(null);
                      }}
                    >
                      <ExternalLink className="mr-1 h-4 w-4" /> Ir al módulo
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
